import { Text } from '@ksyos/design-system';
import { groupBy, orderBy, uniq } from 'lodash';
import { useEffect, useState } from 'react';
import { ManagerAuthorization, ServiceType } from '@common/enums';
import { serviceTypeSortOrder } from '@common/utils/serviceSortUtils';
import { AbortableFetch } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import type { IServiceOrganizationSettings } from '../../../interfaces/serviceOrganizationSettings';
import { OrganizationServiceEmailNotificationServiceGroupSettingsForm } from './OrganizationServiceEmailNotificationServiceGroupSettingsForm';

interface IOrganizationServiceEmailNotificationSettingsEpisodeProps {
    episodeCategory: string;
    organizationId: string;
    serviceGroupToEdit?: string | null;
}

interface IServiceOrganizationSettingsWithAuthorization extends IServiceOrganizationSettings {
    authorization: {
        canUpdate: boolean;
    };
}

export function OrganizationServiceEmailNotificationSettingsEpisode(
    props: IOrganizationServiceEmailNotificationSettingsEpisodeProps
) {
    const abortableFetch = new AbortableFetch();
    const [serviceOrganizationSettings, setServiceOrganizationSettings] = useState<{
        [key: string]: IServiceOrganizationSettingsWithAuthorization[];
    }>({});
    assertHasAuthorization(ManagerAuthorization.organizationRead);

    useEffect(() => {
        loadServices();
    }, [props.episodeCategory, props.organizationId]);

    return (
        <>
            <Text mb={8}>{translateKey('organization.notifications.determineSettingsAsOrganization')}</Text>

            {serviceOrganizationSettings &&
                Object.keys(serviceOrganizationSettings).map((serviceGroup) => {
                    const serviceOrganizationSettingsForServiceGroup = serviceOrganizationSettings[serviceGroup];

                    return (
                        <OrganizationServiceEmailNotificationServiceGroupSettingsForm
                            canEdit={hasOneAuthorization(ManagerAuthorization.organizationAddEdit)}
                            editMode={
                                serviceOrganizationSettingsForServiceGroup[0].service.serviceGroupId ===
                                props.serviceGroupToEdit
                            }
                            formTitle={serviceGroup}
                            key={serviceGroup}
                            onSaved={loadServices}
                            serviceOrganizationSettings={orderBy(
                                serviceOrganizationSettingsForServiceGroup,
                                orderByServiceType
                            )}
                        />
                    );
                })}
        </>
    );

    async function loadServices() {
        const serviceTypes = `serviceTypes[]=${ServiceType.examination}&serviceTypes[]=${ServiceType.consultation}&serviceTypes[]=${ServiceType.referral}`;

        const serviceConsumersUrl = `/api/serviceConsumers?organizationId=${props.organizationId}&${serviceTypes}&episodeCategory=${props.episodeCategory}&isActive=true&includeServiceGroupAndEpisodeCategory=true`;
        const serviceProvidersUrl = `/api/serviceProviders?organizationId=${props.organizationId}&${serviceTypes}&episodeCategory=${props.episodeCategory}&isActive=true&includeServiceGroups=true`;

        const [consumers, providers] = await Promise.all([
            abortableFetch.fetchJson(serviceConsumersUrl, 'GET'),
            abortableFetch.fetchJson(serviceProvidersUrl, 'GET'),
        ]);

        const consumerAndProviderServiceIds = [...consumers.json.items, ...providers.json.items].map(
            (consumerOrProvider) => consumerOrProvider.service.id
        );
        const serviceIds = uniq(consumerAndProviderServiceIds);

        setServiceOrganizationSettings(await loadServiceOrganizationSettings(serviceIds));
    }

    async function loadServiceOrganizationSettings(
        serviceIds: string[]
    ): Promise<{ [key: string]: IServiceOrganizationSettingsWithAuthorization[] }> {
        const url = `/api/serviceOrganizationSettings?organizationId=${props.organizationId}&episodeCategory=${props.episodeCategory}&isActive=true&excludeServicesWithoutServiceGroup=true`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        const serviceOrganizationSettingsList: IServiceOrganizationSettingsWithAuthorization[] = result.json.items;

        const filteredServiceOrganizationSettings = serviceOrganizationSettingsList.filter((settings) =>
            serviceIds.some((serviceId) => serviceId === settings.serviceId)
        );

        return groupBy(filteredServiceOrganizationSettings, 'service.serviceGroupName');
    }

    function orderByServiceType(serviceOrganizationSetting: IServiceOrganizationSettingsWithAuthorization) {
        return serviceTypeSortOrder[serviceOrganizationSetting.service.serviceType];
    }
}
